import { BreakpointObserver, Breakpoints } from '@angular/cdk/layout';
import { Component, ElementRef, Inject, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { AlertLog } from '@app/shared/interfaces/alert-log.interface';
import {
  AlertLogLevel,
  AlertLogStatus,
  AlertLogStatusEnum,
  getAlertLogLevel,
} from '@app/shared/models/alert-log.model';
import { MESSAGES } from '@app/shared/utils/messages';
import { map, Observable, shareReplay, Subject, Subscription, takeUntil } from 'rxjs';
import { getAlertCategory } from '@app/shared/utils/alerts-log';
import { Style, Size as IconSize } from '@app/shared/components/atoms/icon/icon.component';
import { ButtonIcon, ButtonStyle } from '@app/shared/components/atoms/buttons/button.type';
import { Color } from '@app/shared/models/color.model';
import { AlertService } from '@app/shared/components/organisms/alert/alert.service';
import { AuthService } from '@app/shared/auth/auth.service';
import { User } from '@app/shared/models/user-info.model';
import { DateTime } from 'luxon';
import { AlertLogService } from './alert-log.service';
import { BaseTableComponent } from '@app/shared/components/organisms/table/base-table.component';
import { FilterItem } from '@app/shared/components/organisms/table/column-header/column-header.component';
import { formatDateForFilter } from '@app/shared/utils/sort';
import { DateRangePickerComponent } from '@app/shared/components/atoms/date-range-picker/date-range-picker.component';
import { downloadCsv, toCsv } from '@app/shared/utils/utils';
import { TooltipPosition } from '@app/shared/components/atoms/icon/icon.component';

type AlertTodoCsvRow = {
  niveau: string;
  date: string;
  categorie: string;
  message: string;
};

type AlertDoneCsvRow = {
  traitePar: string;
  dateTraitement: string;
  niveau: string;
  date: string;
  categorie: string;
  message: string;
};

const ALERT_TODO_HEADERS: { key: keyof AlertTodoCsvRow; label: string }[] = [
  { key: 'niveau', label: 'Niveau' },
  { key: 'date', label: 'Date' },
  { key: 'categorie', label: 'Catégorie' },
  { key: 'message', label: 'Message' },
];

const ALERT_DONE_HEADERS: { key: keyof AlertDoneCsvRow; label: string }[] = [
  { key: 'traitePar', label: 'Traitée par' },
  { key: 'dateTraitement', label: 'Traitée le' },
  { key: 'niveau', label: 'Niveau' },
  { key: 'date', label: 'Date' },
  { key: 'categorie', label: 'Catégorie' },
  { key: 'message', label: 'Message' },
];

@Component({
  selector: 'app-alert-log',
  templateUrl: './alert-log.component.html',
  styleUrls: ['./alert-log.component.scss'],
  standalone: false,
})
export class AlertLogComponent implements OnDestroy, OnInit {
  @ViewChild('inputElement') inputElement!: ElementRef;
  inputValue: string = '';

  @ViewChild(DateRangePickerComponent) dateRangePickerComponent!: DateRangePickerComponent;

  @ViewChild(BaseTableComponent, { static: false })
  private baseTable!: BaseTableComponent<AlertLog>;

  AlertLogStatus = AlertLogStatus;

  alertLogStatusSelected: AlertLogStatus;

  AlertLogEnum = AlertLogStatusEnum;

  AlertLogLevel = AlertLogLevel;

  getAlertCategory = getAlertCategory;

  getAlertLogLevel = getAlertLogLevel;

  alertsSource: AlertLog[] = [];

  alertsSourceSearch: AlertLog[] = [];

  isLoading: boolean = true;

  rangeStart: string = '';

  rangeEnd: string = '';

  currentSearchValue = '';

  alertsRequest?: Subscription;

  unsubscribe$: Subject<boolean> = new Subject<boolean>();

  protected readonly TooltipPosition = TooltipPosition;

  protected readonly Style = Style;

  protected readonly ButtonStyle = ButtonStyle;

  protected readonly ButtonIcon = ButtonIcon;

  protected readonly Color = Color;

  protected readonly IconSize = IconSize;

  filterItems: { [key: string]: FilterItem[] } = {};

  isHandset$: Observable<boolean> = this.breakpointObserver.observe(Breakpoints.Handset).pipe(
    map((result) => result.matches),
    shareReplay(),
  );

  userConnected?: User;

  constructor(
    private breakpointObserver: BreakpointObserver,
    @Inject(ActivatedRoute) private route: ActivatedRoute,
    private alertLogService: AlertLogService,
    private alertService: AlertService,
    private authService: AuthService,
  ) {
    this.authService.connectedUser$.pipe(takeUntil(this.unsubscribe$)).subscribe((user) => {
      if (user) this.userConnected = user;
    });
    this.alertLogStatusSelected = AlertLogStatus.TODO;
  }

  ngOnInit() {
    this.route.queryParams.subscribe(() => {
      this.isLoading = true;
      this.alertsSource = [];
      this.alertsSourceSearch = [];
      this.alertsRequest = this.getAlertLogRequest({ status: AlertLogStatus.TODO });
    });
  }

  generateFilterItems = (headerList: (keyof AlertLog)[]) => {
    const setsMap = new Map<keyof AlertLog, Set<string>>();
    this.filterItems = {};

    headerList.forEach((field) => {
      setsMap.set(field, new Set<string>());
      this.filterItems[field] = [];
    });

    this.alertsSource.forEach((item) => {
      headerList.forEach((field) => {
        const fieldValue = item[field] as string;

        if (!setsMap.get(field)?.has(fieldValue)) {
          setsMap.get(field)?.add(fieldValue);
          this.filterItems[field].push({ name: fieldValue, checked: false });
        }
      });
    });
  };

  private getAlertLogRequest(filter: { [key: string]: string }) {
    this.isLoading = true;
    return this.alertLogService.getAlertsLog(filter).subscribe({
      next: (response) => {
        this.alertsSource = response.Items;
        this.alertsSourceSearch = response.Items;
        if (filter.status === AlertLogStatus.DONE) {
          const doneFiltredHeaderList = ['email', 'level', 'category'];
          this.generateFilterItems(doneFiltredHeaderList as (keyof AlertLog)[]);
        } else {
          const todoFiltredHeaderList = ['level', 'category'];
          this.generateFilterItems(todoFiltredHeaderList as (keyof AlertLog)[]);
        }
        this.getAlertsSearch(this.currentSearchValue);
        this.isLoading = false;
      },
      error: (error) => {
        console.error(`Erreur lors de la récupération des alertes au statut : ${filter.status}`, error);
        this.isLoading = false;
      },
    });
  }

  alertStatusChange(event: AlertLogStatusEnum) {
    this.isLoading = true;

    this.inputValue = '';
    this.dateRangePickerComponent.clearDates();

    const statusMapping = {
      [AlertLogStatusEnum.TODO]: AlertLogStatus.TODO,
      [AlertLogStatusEnum.DONE]: AlertLogStatus.DONE,
    };

    const status = statusMapping[event];

    if (status !== undefined) {
      this.alertsSource = [];
      this.alertsSourceSearch = [];

      this.alertsRequest = this.getAlertLogRequest({ status });
      this.alertLogStatusSelected = status;
    }
  }

  /**
   * Méthode permettant de mettre a jour l'alert
   * @param i L'index de la facture
   * @param status Le status à mettre en bdd
   */
  protected putAlert(data: AlertLog, status: AlertLogStatus) {
    this.alertLogService
      .putAlertsLog({
        timestamp: data.timestamp,
        status,
        email: this.userConnected?.email,
        closeDate: DateTime.now().setZone('Europe/Paris').toMillis(),
      })
      .subscribe({
        next: () => {
          this.alertsSource = this.alertsSource.filter(({ timestamp }) => timestamp !== data.timestamp);
          this.alertsSourceSearch = this.alertsSourceSearch.filter(({ timestamp }) => timestamp !== data.timestamp);
        },
        error: () => this.alertService.error(MESSAGES.DOWNLOAD_ERROR),
      });
  }

  ngOnDestroy(): void {
    this.unsubscribe$.next(true);
    this.unsubscribe$.complete();
  }

  /**
   * Méthode permettant de chercher en fonction de différents critères
   * @param searchValue La valeur à rechercher
   */
  getAlertsSearch(searchValue: string) {
    this.isLoading = true;
    console.log();
    this.currentSearchValue = searchValue;

    if (searchValue === '') {
      this.alertsSourceSearch = this.alertsSource;
    } else {
      this.alertsSourceSearch = this.alertsSource.filter((item) => this.matches(item, searchValue));
    }

    this.isLoading = false;

    this.baseTable?.resetPage();
  }

  /**
   * Vérifie si un élément (item) correspond à la valeur de recherche (searchValue).
   * @param item L'élément à vérifier.
   * @param searchValue La valeur de recherche à comparer.
   * @returns true si l'élément correspond à la valeur de recherche, sinon false.
   */
  matches(item: AlertLog, searchValue: string): boolean {
    const lowerSearchValue = searchValue.toLowerCase();

    return ['category', 'message'].some((key) => {
      const value = item[key as keyof AlertLog];
      if (!value) return false;

      let formattedValue: string;
      if (key === 'category') {
        formattedValue = getAlertCategory(value as string);
      } else {
        formattedValue = value as string;
      }

      return formattedValue.toLowerCase().includes(lowerSearchValue);
    });
  }

  async onStartDateChanged(newStartDate: string) {
    this.rangeStart = newStartDate;
    const startDate = formatDateForFilter(newStartDate, 'INF');
    const endDate = formatDateForFilter(this.rangeEnd, 'SUP');

    this.isLoading = true;

    await this.getAlertLogRequest({
      status: this.alertLogStatusSelected,
      startDate,
      endDate,
    });

    this.baseTable?.resetPage();
    this.isLoading = false;
  }

  async onEndDateChanged(newEndDate: string) {
    this.rangeEnd = newEndDate;
    const endDate = formatDateForFilter(newEndDate, 'SUP');
    const startDate = formatDateForFilter(this.rangeStart, 'INF');

    this.isLoading = true;

    await this.getAlertLogRequest({
      status: this.alertLogStatusSelected,
      startDate,
      endDate,
    });

    this.baseTable?.resetPage();
    this.isLoading = false;
  }

  /**
   * Méthode pour exporter les alertes dans le tableau
   */
  exportAlerts() {
    if (this.alertLogStatusSelected === AlertLogStatus.TODO) {
      this.exportTodoAlerts();
    } else {
      this.exportDoneAlerts();
    }
  }

  private exportTodoAlerts() {
    const rows: AlertTodoCsvRow[] = this.alertsSourceSearch.map((alert) => ({
      niveau: this.getAlertLogLevel(alert.level),
      categorie: this.getAlertCategory(alert.category),
      message: alert.message,
      date: DateTime.fromMillis(alert.timestamp).toFormat('dd/MM/yyyy HH:mm'),
    }));

    downloadCsv(
      `billing_alertes_a_traiter_${DateTime.now().toFormat('yyyyLLdd')}.csv`,
      toCsv(rows, ALERT_TODO_HEADERS),
    );
  }

  private exportDoneAlerts() {
    const rows: AlertDoneCsvRow[] = this.alertsSourceSearch.map((alert) => ({
      traitePar: alert.email,
      dateTraitement: DateTime.fromMillis(alert.closeDate!).toFormat('dd/MM/yyyy HH:mm'),
      niveau: this.getAlertLogLevel(alert.level),
      categorie: this.getAlertCategory(alert.category),
      message: alert.message,
      date: DateTime.fromMillis(alert.timestamp).toFormat('dd/MM/yyyy HH:mm'),
    }));

    downloadCsv(`billing_alertes_traitees_${DateTime.now().toFormat('yyyyLLdd')}.csv`, toCsv(rows, ALERT_DONE_HEADERS));
  }
}
